import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { FiX } from "react-icons/fi";
// Reuse the upgrade-pro-modal styling — same card, icon badge, info box,
// stacked CTA buttons and mobile bottom-sheet/swipe-to-close behaviour as
// CityDetailLimitModal, just with login/signup copy and actions instead of
// plan upsell ones.
import "./ComparisonModal.scss";

const SWIPE_CLOSE_THRESHOLD = 110;
const SWIPE_MOBILE_BREAKPOINT = 576;

const LockIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

// Shown when a guest (no logged-in session) tries to use an auth-only
// action on a page that otherwise allows guest viewing — e.g. Save,
// View on Map, Add Expenses on the job-detail page.
const AuthRequiredModal = ({
  isVisible,
  onClose,
  heading = "Login Required!",
  subText = "Log in or sign up to continue.",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = `${location.pathname}${location.search}`;

  const handleLogin = () => {
    onClose && onClose();
    navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
  };

  const handleSignup = () => {
    onClose && onClose();
    navigate("/registration");
  };

  /* Mobile-only swipe-down-to-close (same gesture as CityDetailLimitModal). */
  const dialogRef = useRef(null);
  const startYRef = useRef(null);
  const draggingRef = useRef(false);
  const [dragY, setDragY] = useState(0);

  const isMobile = () =>
    typeof window !== "undefined" &&
    window.innerWidth <= SWIPE_MOBILE_BREAKPOINT;

  const onTouchStart = (e) => {
    if (!isMobile()) return;
    startYRef.current = e.touches[0].clientY;
    draggingRef.current = true;
  };
  const onTouchMove = (e) => {
    if (!draggingRef.current || startYRef.current == null) return;
    const dy = e.touches[0].clientY - startYRef.current;
    setDragY(dy > 0 ? dy : 0);
  };
  const onTouchEnd = () => {
    if (!draggingRef.current) return;
    const finalDy = dragY;
    draggingRef.current = false;
    startYRef.current = null;
    if (finalDy > SWIPE_CLOSE_THRESHOLD) {
      setDragY(0);
      onClose && onClose();
    } else {
      setDragY(0);
    }
  };

  useEffect(() => {
    if (!isVisible) setDragY(0);
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    const content = document.querySelector(".upgrade-pro-modal .modal-content");
    if (!content) return;
    if (dragY > 0) {
      content.style.transform = `translateY(${dragY}px)`;
      content.style.transition = "none";
    } else {
      content.style.transform = "";
      content.style.transition = "";
    }
  }, [dragY, isVisible]);

  return (
    <Modal
      show={isVisible}
      onHide={onClose}
      centered
      backdrop={true}
      dialogClassName="upgrade-pro-modal"
      backdropClassName="upgrade-pro-modal-backdrop"
    >
      <Modal.Body
        ref={dialogRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        <div
          className="upm-handle"
          aria-hidden="true"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
        />

        <button
          type="button"
          className="upm-close"
          onClick={onClose}
          aria-label="Close"
        >
          <FiX size={20} />
        </button>

        <div className="upm-icon-row">
          <div className="upm-icon">
            <LockIcon className="upm-icon-svg" />
          </div>
          <span className="upm-badge-limit">Members only</span>
        </div>

        <h2 className="upm-title">{heading}</h2>
        <p className="upm-sub">Create a free account to unlock this.</p>

        <div className="upm-info">
          <p className="upm-info-text">{subText}</p>
        </div>

        <div className="upm-ctas">
          <button
            type="button"
            className="upm-btn-primary"
            onClick={handleSignup}
          >
            <span className="upm-btn-primary-title">Sign Up</span>
            <span className="upm-btn-primary-sub">
              It's free — takes less than a minute
            </span>
          </button>

          <button
            type="button"
            className="upm-btn-secondary"
            onClick={handleLogin}
          >
            Log In
          </button>
        </div>

        <button type="button" className="upm-later" onClick={onClose}>
          Not now
        </button>
      </Modal.Body>
    </Modal>
  );
};

export default AuthRequiredModal;
